/* eslint-disable*/
import { useState, useContext, useEffect } from 'react';

import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Avatar,
  Chip,
  Badge,
  useTheme,
} from '@mui/material';

import {
  Menu as MenuIcon,
  ChevronLeft,
  Cached,
  Brightness4,
  Brightness7,
  Person,
  Logout,
  LogoutRounded,
  DriveFileRenameOutline,
} from '@mui/icons-material';

import MainListItems from './MainListItems';
import NotificationsVolume from '../components/NotificationsVolume';
import UserModal from '../components/UserModal';
import { AuthContext } from '../context/Auth/AuthContext';
import BackdropLoading from '../components/BackdropLoading';
import { i18n } from '../translate/i18n';
import toastError from '../toasts/toastError';

import ColorModeContext from './themeContext';
import { getBackendUrl } from '../config';

import { getLayoutStyled } from './styles';
import ButtonWithSpinner from '../components/ButtonWithSpinner';

const backendUrl = getBackendUrl();

const LoggedInLayout = ({ children }) => {
  const theme = useTheme();
  const classes = getLayoutStyled(theme);

  const [userToken] = useState('disabled');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState('permanent');

  const { colorMode } = useContext(ColorModeContext);

  const [volume, setVolume] = useState(localStorage.getItem('volume') || 1);
  const [profileUrl, setProfileUrl] = useState(null);

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === 'closed') {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }
    if (user.defaultTheme === 'dark' && theme.mode === 'light') {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant('temporary');
    } else {
      setDrawerVariant('permanent');
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        setProfileUrl(
          `${backendUrl}/public/company${companyId}/user/${ImageUrl}`,
        );
      else setProfileUrl(`${import.meta.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError('Sua conta foi acessada em outro computador.');
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit('userStatus');
      const interval = setInterval(
        () => {
          socket.emit('userStatus');
        },
        1000 * 60 * 5,
      );

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
  }, [socket]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div style={classes.root}>
      <Drawer
        variant={drawerVariant}
        open={drawerOpen}
        slotProps={{
          paper: {
            style: drawerOpen
              ? classes.drawerPaper
              : { ...classes.drawerPaper, ...classes.drawerPaperClose },
          },
        }}
      >
        <div style={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeft sx={{ color: theme.custom.colors.text }} />
          </IconButton>
        </div>

        <List sx={classes.containerWithScroll}>
          <MainListItems collapsed={!drawerOpen} />
        </List>

        <Divider sx={{ borderBottomColor: theme.custom.colors.text }} />
      </Drawer>

      <AppBar
        position='absolute'
        sx={
          drawerOpen
            ? { ...classes.appBar, ...classes.appBarShift }
            : classes.appBar
        }
      >
        <Toolbar variant='dense' style={classes.toolbar}>
          <IconButton
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ color: 'white', transform: 'translateX(-40%)' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography sx={classes.title} noWrap />

          {userToken === 'enabled' && user?.companyId === 1 && (
            <Chip
              sx={classes.chip}
              label={i18n.t('mainDrawer.appBar.user.token')}
            />
          )}

          <IconButton onClick={colorMode.toggleColorMode}>
            {theme.palette.mode === 'dark' ? (
              <Brightness7 sx={{ color: 'white' }} />
            ) : (
              <Brightness4 sx={{ color: 'white' }} />
            )}
          </IconButton>

          <NotificationsVolume setVolume={setVolume} volume={volume} />

          <IconButton onClick={handleRefreshPage}>
            <Cached sx={{ color: 'white' }} />
          </IconButton>

          <Badge
            overlap='circular'
            variant='dot'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{ cursor: 'pointer' }}
            onClick={handleMenu}
          >
            <Avatar sx={classes.avatar2} src={profileUrl} />
          </Badge>

          <UserModal
            open={userModalOpen}
            onClose={() => setUserModalOpen(false)}
            onImageUpdate={setProfileUrl}
            userId={user?.id}
          />

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            {...theme.custom.menuProps}
          >
            <MenuItem sx={classes.menuItemCenter}>
              <Person sx={classes.iconAccount} />
              {user.name}
            </MenuItem>

            <MenuItem onClick={handleOpenUserModal} sx={classes.menuItemCenter}>
              <DriveFileRenameOutline sx={classes.iconAccount} />
              {i18n.t('mainDrawer.appBar.user.profile')}
            </MenuItem>

            <MenuItem onClick={handleClickLogout} sx={classes.menuItemCenter}>
              <LogoutRounded sx={classes.iconAccount} />

              {i18n.t('mainDrawer.appBar.user.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <main style={classes.content}>
        <div style={classes.appBarSpacer} />
        {children}
      </main>
    </div>
  );
};

export default LoggedInLayout;
